import './GiftInvitationPrompt.css';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { GiftHelpPreview } from './full/GiftHelpPreview';
import { DEFAULT_GIFT_PRESET } from './full/giftPreviewPresets';
import './full/GifteeFramePreview.css';
import { DEFAULT_GAME_SEASON, isSeason } from '@src/theme/season';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Hint, Link } from '@src/components';
import { closeDialog, showDialog, subscribeDialog } from '@src/components/Dialog/Dialog';
import { useAppSelector } from '@src/state/hooks';
import { boxPath } from '@src/routing/routes';

type Invitation = {
  id: string;
  giftId: number;
  audience?: 'users' | 'boxes';
  status: string;
  expiresAt: string;
  boxKey: string;
  boxName: string;
  boxTheme: string | null;
  serverNow: string;
};
async function request(path: string, body: unknown): Promise<Invitation | null> {
  const res = await fetch(`/api/gift-invitations/${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw Error('Invitation request failed');
  return (await res.json()).invitation;
}
function giftDestination(boxKey: string) {
  const destination = new URL(boxPath(boxKey), window.location.origin);
  const current = new URLSearchParams(window.location.search);
  for (const key of ['automaticPayment', 'price']) {
    if (current.has(key)) destination.searchParams.set(key, current.get(key)!);
  }
  return destination.pathname + destination.search;
}
function InvitationBody({ invitation }: { invitation: Invitation }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [deadline] = useState(
    () =>
      Date.now() + Math.max(0, Date.parse(invitation.expiresAt) - Date.parse(invitation.serverNow)),
  );
  const [seconds, setSeconds] = useState(() => Math.ceil((deadline - Date.now()) / 1000));
  const deadlineRef = useRef(deadline);
  const accepting = useRef(false);
  const acceptedIntent = useRef(false);
  useEffect(() => {
    let active = true;
    const check = () => {
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setSeconds(left);
      if (!left && !accepting.current) {
        sync(); // Confirm expiry server-side in case an admin just extended the reservation.
      }
    };
    const sync = () =>
      void request('action', { id: invitation.id, action: 'shown' })
        .then((current) => {
          if (active && current?.status === 'locked') {
            deadlineRef.current = Date.now() + Math.max(0, Date.parse(current.expiresAt) - Date.parse(current.serverNow));
            setSeconds(Math.ceil((deadlineRef.current - Date.now()) / 1000));
          }
          if (active && current && current.status !== 'locked' && !accepting.current) {
            if (current.status === 'accepted' && acceptedIntent.current)
              window.location.assign(giftDestination(current.boxKey));
            else closeDialog();
          }
        })
        .catch(() => {});
    sync();
    const clock = window.setInterval(check, 1000);
    const poll = window.setInterval(sync, 5000);
    document.addEventListener('visibilitychange', check);
    return () => {
      active = false;
      clearInterval(clock);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', check);
    };
  }, [invitation.id, deadline]);
  async function accept() {
    if (busy || seconds <= 0) return;
    acceptedIntent.current = true;
    accepting.current = true;
    setBusy(true);
    setError(false);
    try {
      const current = await request('action', { id: invitation.id, action: 'accept' });
      if (current?.status === 'accepted') {
        // Reload even on the same box so its landscape fetches the newly assigned gift.
        window.location.assign(giftDestination(current.boxKey));
      } else closeDialog();
    } catch {
      setError(true);
    } finally {
      accepting.current = false;
      setBusy(false);
    }
  }
  return (
    <div className="gift-invitation" data-testid="gift-invitation-body">
      <p className="gift-invitation__message">{t(invitation.audience === 'boxes' ? 'giftInvitation.boxMessage' : 'giftInvitation.message')}</p>
      <p className="gift-invitation__hint">{t('giftInvitation.hint')}</p>
      <div className="gift-invitation__scene">
      <GiftHelpPreview device="desktop" preset={DEFAULT_GIFT_PRESET} season={isSeason(invitation.boxTheme) ? invitation.boxTheme : DEFAULT_GAME_SEASON} />
      </div>
      <div className="gift-invitation__countdown">
        <p className="gift-invitation__countdown-label"><ClockIcon />{t('giftInvitation.timerLabel')}</p>
        <div className="gift-invitation__countdown-time" role="timer" aria-live="off" aria-label={t('giftInvitation.timerLabel')}>
          {`${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`}
        </div>
      </div>
      <p className="gift-invitation__expiry-note">{t('giftInvitation.expiryNote')}</p>
      {error && <Hint tone="error">{t('giftInvitation.error')}</Hint>}
      <Button className="gift-invitation__open" loading={busy} disabled={busy || seconds <= 0} onClick={() => void accept()}>
        <span className="gift-invitation__open-label"><span aria-hidden="true">✨</span> {t(invitation.audience === 'boxes' ? 'giftInvitation.boxGo' : 'giftInvitation.go')} <GiftIcon /> <span aria-hidden="true">→</span></span>
      </Button>
      <div className="gift-invitation__dismiss">
      <Link
        variant="quiet"
        onClick={() => {
          if (busy) return;
          closeDialog();
          void request('action', { id: invitation.id, action: 'dismiss' }).catch(() => {});
        }}
      >
        {t('giftInvitation.dismiss')}
      </Link>
      </div>
    </div>
  );
}

export function GiftInvitationPrompt() {
  const user = useAppSelector((s) => s.session.user);
  const userId = user?.id;
  const location = useLocation();
  const { t } = useTranslation();
  const dialog = useRef(false);
  const dirty = useRef(false);
  useEffect(
    () =>
      subscribeDialog((item) => {
        dialog.current = Boolean(item);
      }),
    [],
  );
  useEffect(() => {
    dirty.current = false;
    const input = (event: Event) => {
      if ((event.target as HTMLElement)?.matches('input,textarea,select,[contenteditable="true"]'))
        dirty.current = true;
    };
    document.addEventListener('input', input);
    return () => document.removeEventListener('input', input);
  }, [location.pathname]);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let inFlight = false;
    let checked = false;
    const safe = () =>
      !dialog.current &&
      !dirty.current &&
      document.visibilityState === 'visible' &&
      !document.querySelector('form, [role="dialog"], [contenteditable="true"]') &&
      !document.activeElement?.matches('input,textarea,select') &&
      (location.pathname === '/' ||
        location.pathname === '/account/boxes' ||
        /^\/box\/[^/]+(?:\/exchange)?\/?$/.test(location.pathname));
    const check = async () => {
      if (cancelled || checked || inFlight || !safe()) return;
      inFlight = true;
      try {
        const invitation = await request('reserve', {});
        if (cancelled) return; // Reservation expires server-side if navigation interrupts the response.
        if (!invitation) {
          checked = true;
          return;
        }
        if (!safe()) return;
        checked = true;
        showDialog({
          title: <span className="gift-invitation__title">{t('giftInvitation.lucky')}</span>,
          testId: 'gift-invitation',
          hideClose: true,
          children: <InvitationBody invitation={invitation} />,
          onCancel: () => {
            void request('action', { id: invitation.id, action: 'dismiss' }).catch(() => {});
          },
        });
      } catch {
        /* Optional invitation must not interrupt the user's task. Retry on a later visit. */ checked =
          true;
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(() => void check(), 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [userId, location.pathname, t]);
  return null;
}
